import React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Box, Typography } from "@mui/material";
import AppDocuServiceCall from "../_Utils/AppDocuServiceCall";
import { notifySomething } from "../_Utils/AppDocuCommon";

export default class VehicleImageDialog extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            imageUrl: null,
            imageFile: null,
            imagePreviewUrl: null,
            isDragging: false,
        };
        this._imageInputRef = React.createRef();
    }

    async componentDidUpdate(prevProps) {
        if (this.props.vehicleId && this.props.vehicleId !== prevProps.vehicleId) {
            this.setState({ loading: true, imageUrl: null, imageFile: null, imagePreviewUrl: null, isDragging: false });
            try {
                const imgRes = await AppDocuServiceCall.getVehicleImage(this.props.vehicleId);
                const url = URL.createObjectURL(imgRes.data);
                this.setState({ imageUrl: url, loading: false });
            } catch (e) {
                this.setState({ loading: false });
                // 404 is fine, silent fail
            }
        }
    }

    handleClose = () => {
        if (this.state.imageUrl) URL.revokeObjectURL(this.state.imageUrl);
        if (this.state.imagePreviewUrl) URL.revokeObjectURL(this.state.imagePreviewUrl);
        this.setState({ imageUrl: null, imageFile: null, imagePreviewUrl: null, isDragging: false });
        this.props.onClose();
    };

    handleImageFileChange = (e) => {
        const file = e.target.files[0];
        this.setNewImageFile(file);
        e.target.value = '';
    };

    setNewImageFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        if (this.state.imagePreviewUrl) URL.revokeObjectURL(this.state.imagePreviewUrl);
        const previewUrl = URL.createObjectURL(file);
        this.setState({ imageFile: file, imagePreviewUrl: previewUrl });
    };

    handleDragOver = (e) => {
        e.preventDefault();
        this.setState({ isDragging: true });
    };

    handleDragLeave = () => {
        this.setState({ isDragging: false });
    };

    handleDrop = (e) => {
        e.preventDefault();
        this.setState({ isDragging: false });
        const file = e.dataTransfer.files[0];
        this.setNewImageFile(file);
    };

    handlePaste = (e) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
                const blob = items[i].getAsFile();
                this.setNewImageFile(blob);
                break;
            }
        }
    };

    handleSetImage = async () => {
        const { imageFile } = this.state;
        if (!imageFile) return;
        this.setState({ loading: true });
        try {
            const res = await AppDocuServiceCall.setVehicleImage(this.props.vehicleId, imageFile);
            if (res.data && res.data.is_valid === false) {
                notifySomething(res.data.message || 'Error setting image', 'error');
            } else {
                notifySomething('Image updated', 'success');
                if (this.props.onItemsChange) await this.props.onItemsChange();
                this.handleClose();
            }
        } catch (e) {
            notifySomething('Error setting image', 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleRemoveImage = async () => {
        if (!window.confirm('Remove this image?')) return;
        this.setState({ loading: true });
        try {
            const res = await AppDocuServiceCall.removeVehicleImage(this.props.vehicleId);
            if (res.data && res.data.is_valid === false) {
                notifySomething(res.data.message || 'Error removing image', 'error');
            } else {
                notifySomething('Image removed', 'success');
                if (this.props.onItemsChange) await this.props.onItemsChange();
                this.handleClose();
            }
        } catch (e) {
            notifySomething('Error removing image', 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { loading, imageUrl, imagePreviewUrl, isDragging, imageFile } = this.state;
        const previewSrc = imagePreviewUrl || imageUrl;
        return (
            <Dialog open={!!this.props.vehicleId} onClose={this.handleClose} maxWidth="xs" fullWidth onPaste={this.handlePaste}>
                <DialogTitle>Vehicle Image</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
                        <Box 
                            onDragOver={this.handleDragOver}
                            onDragLeave={this.handleDragLeave}
                            onDrop={this.handleDrop}
                            sx={{ 
                                width: '100%',
                                aspectRatio: '4 / 3',
                                border: isDragging ? '2px dashed #1976d2' : '1px solid #ccc', 
                                borderRadius: 1, 
                                overflow: 'hidden', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                background: isDragging ? '#e3f2fd' : '#f0f0f0',
                                transition: 'all 0.2s',
                                cursor: 'pointer'
                            }}
                            onClick={() => this._imageInputRef.current.click()}
                        >
                            {previewSrc ? (
                                <img src={previewSrc} alt="Vehicle" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Drop image here, paste (Ctrl+V) <br/> or click to upload
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        
                        <input ref={this._imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={this.handleImageFileChange} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                            <Button variant="outlined" size="small" fullWidth onClick={() => this._imageInputRef.current.click()}>
                                {previewSrc ? 'Change Image' : 'Upload Image'}
                            </Button>
                            {imageUrl && (
                                <Button variant="outlined" size="small" color="error" fullWidth onClick={this.handleRemoveImage} disabled={loading}>
                                    Remove Current Image
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleClose}>Cancel</Button>
                    <Button onClick={this.handleSetImage} color="primary" disabled={loading || !imageFile}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}
